import React, { ChangeEvent, useState } from 'react';
import { Select, Checkbox, Button, Icon } from '@gerdau/hefesto/';
import { DateField } from '@gerdau/hefesto-mais-web/';

import Product from './Product';
import ProductCartModal from './ProductCartModal';

import {
  Container,
  ProductTableContainer,
  Text,
  BoxActionContainer,
  RowActionContainer,
  ChipContainer,
  ChipTitle
} from './style';
import {
  CartOptions,
  Destination,
  ExtensionAttributes,
  FiscalBenefits,
  ItemTableProps,
  NewCartItem,
  NewCartOption,
  PaymentTerm,
  SalesAgreementData,
  UpdateItemProps
} from '../../../../../services/MVP/models';
import { DateType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import moment from 'moment';
import { ValidationModal } from '../ValidationModal';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { useDispatch } from 'react-redux';
import {
  paymentAnticipated,
  purchaseOrigin
} from '../../../../../services/MVP/utils';
import { GetFlag } from '../../../../../utils/featureFlag';
import { isDateBeyondMaxDelivery } from '../../../../../utils/formatDate';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../store/ducks/toast/types';

export interface OptionsProps {
  date?: DateType;
  payment?: string | undefined;
  fiscalBenefits?: string | undefined;
  destination?: string | undefined;
  updatePrice?: boolean;
  closeWhenSucess?: boolean;
}

const DEFAULT_OPTIONS = {
  date: null,
  payment: undefined,
  fiscalBenefits: undefined,
  destination: undefined
};

const ChipSelectedItems = ({
  selectedItems,
  allItems,
  selectedAll
}: {
  selectedItems: number;
  allItems: number;
  selectedAll: boolean;
}) => {
  if (selectedItems === 0) {
    return null;
  }

  return (
    <ChipContainer>
      <ChipTitle gray>
        {selectedAll ? allItems : selectedItems} de {allItems} selecionados
      </ChipTitle>
    </ChipContainer>
  );
};

const checkAgreement = ({
  agreementData,
  extensionAttributes
}: {
  agreementData: SalesAgreementData;
  extensionAttributes: ExtensionAttributes;
}) => {
  const productDate = extensionAttributes.expected_date_gerdau
    ? extensionAttributes.expected_date_gerdau.replace('Z', '')
    : extensionAttributes.expected_date_gerdau;

  const endDate = agreementData.expirationDate
    ? agreementData.expirationDate.replace('Z', '')
    : agreementData.expirationDate;

  const salesAgreementExpirationDate = !moment(productDate).isAfter(endDate);

  const hasAnyAgreementKeyEmpty = Object.values(agreementData).some(
    (item) => !item
  );
  if (agreementData && !hasAnyAgreementKeyEmpty) {
    return (
      agreementData.destination === extensionAttributes.destination_gerdau &&
      agreementData.paymentTerms === extensionAttributes.payment_type_gerdau &&
      salesAgreementExpirationDate
    );
  }

  return false;
};

const validationTitle = {
  destination: 'Alterar a destinação afetará o preço acordado',
  payment: 'Alterar a condição de pagamento afetará o preço acordado',
  fiscalBenefit: 'Alterar o benefício fiscal afetará o preço acordado',
  date: 'Alterar a data desejada afetará o preço acordado'
};

const validationDescription = {
  destination:
    'Será necessário recalcular o preço após essa alteração. Deseja alterar a destinação?',
  payment:
    'Será necessário recalcular o preço após essa alteração. Deseja alterar a condição de pagamento?',
  fiscalBenefit:
    'Será necessário recalcular o preço após essa alteração. Deseja alterar o benefício fiscal?',
  date: 'Será necessário recalcular o preço após essa alteração. Deseja alterar a data desejada?'
};

const CartProductTable = ({
  selectedItems,
  allItems,
  dirtyCartItems,
  itemsTable,
  handleUpdateItem,
  handleDeleteItem,
  handleDeleteAllItems,
  handleUpdateAllItems,
  handleUpdateSingleItem,
  handleDuplicateItem,
  handleSelectAll,
  handleSelectProduct,
  isBlockedCustomer,
  loading,
  syncingFields = new Map(),
  allItemsFailed,
  shoppingCartRef,
  favoritedSkus = new Set<string>(),
  handleWishlistToggle
}: {
  shoppingCartRef: React.RefObject<HTMLDivElement>;
  selectedItems: number[];
  allItems: NewCartItem[];
  dirtyCartItems: NewCartItem[];
  itemsTable: ItemTableProps;
  handleUpdateItem: (props: UpdateItemProps) => void;
  handleDeleteItem: (id: number) => void;
  handleDeleteAllItems: () => Promise<void>;
  handleUpdateAllItems: (props: OptionsProps) => void;
  handleUpdateSingleItem?: (product: NewCartItem) => void;
  handleDuplicateItem: (id: number) => void;
  handleSelectAll: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSelectProduct: (
    e: ChangeEvent<HTMLInputElement>,
    item_id: number
  ) => void;
  isBlockedCustomer: boolean;
  loading: boolean;
  syncingFields?: Map<number, Set<UpdateItemProps['type']>>;
  allItemsFailed?: boolean;
  favoritedSkus?: Set<string>;
  handleWishlistToggle?: (sku: string) => void;
}) => {
  if (loading || itemsTable.items.length === 0 || allItems.length === 0) {
    return null;
  }

  const newCartEndpoint = GetFlag('new_cart_endpoints');
  const isFavoriteEnabled = GetFlag('favorite_product');

  const handleToggleFavorite = handleWishlistToggle;

  const tipo_compra = purchaseOrigin();
  const dispatch = useDispatch();
  const selectedSome = selectedItems.length >= 1;
  const selectedAll = allItems.length === selectedItems.length;
  const [openModal, setOpenModal] = useState(false);
  const [optionsSelect, setOptionsSelect] =
    useState<OptionsProps>(DEFAULT_OPTIONS);
  const [validationModalInformation, setValidationModalInformation] = useState<{
    show: boolean;
    title: string;
    description: string;
    value: string | string[];
    handleNextBtnTitle: string;
    handleNextBtn: () => void;
    handleCloseValidationModal: () => void;
  }>({
    show: false,
    title: '',
    description: '',
    value: '',
    handleNextBtn: () => null,
    handleNextBtnTitle: 'Alterar',
    handleCloseValidationModal: () => null
  });

  const allOptions = allItems
    .map((opt) => opt.cartOptions)
    .reduce((acc: CartOptions[], item: NewCartOption) => {
      const options = Object.values(item);
      return [...acc, ...options];
    }, []);

  const afterHalfDay = moment()
    .utcOffset(-3)
    .isAfter(moment().utcOffset(-3).set({ hour: 12, minute: 0, second: 0 }));

  /**
   * ADCL-6410
   * Alteração na lógica para considerar a data minima
   */

  const minDate = moment()
    .utcOffset(-3)
    .add(afterHalfDay ? 5 : 4, 'day')
    .toDate();

  const destinations = allOptions
    .map((opt) => opt.destination)
    .reduce((acc: Destination[], item: Destination) => {
      if (acc.some((accItem) => accItem.code === item.code)) {
        return acc;
      }

      return [...acc, item];
    }, []);
  const destinationsOpt = destinations.map((dest) => ({
    value: dest.code,
    label: dest.description
  }));

  const paymentsTerm = allOptions
    .map((opt) => opt.paymentTerms)
    .reduce((acc, item) => [...item, ...acc], [])
    .reduce((accAll: PaymentTerm[], item: PaymentTerm) => {
      if (accAll.some((accItem) => accItem.term === item.term)) {
        return accAll;
      }

      return [...accAll, item];
    }, [])
    .filter((opt) =>
      isBlockedCustomer ? paymentAnticipated.includes(opt.term) : opt
    );
  const paymentsTermOpt = paymentsTerm.map((payment) => ({
    value: payment.term,
    label: payment.description
  }));

  const fiscalBenefits = allOptions
    .map((opt) => opt.fiscalBenefits)
    .reduce((acc, item) => [...item, ...acc], [])
    .reduce((accAll: FiscalBenefits[], item: FiscalBenefits) => {
      if (accAll.some((accItem) => accItem.code === item.code)) {
        return accAll;
      }

      return [...accAll, item];
    }, []);
  const fiscalBenefitsOpt =
    fiscalBenefits.length > 0
      ? fiscalBenefits
          .map((tax) => ({
            value: tax?.code,
            label: !tax?.description && tax?.code ? tax?.code : tax?.description
          }))
          .filter((tax) => tax?.label && tax?.label?.length !== 0)
      : [{ value: 'Indisponível', label: 'Indisponível' }];

  const handleOpenModal = () => setOpenModal(true);
  const handleModalAction = async (withDelete?: boolean) => {
    if (withDelete) {
      await handleDeleteAllItems();
    }

    return setOpenModal(false);
  };

  const handleUpdateWithAllOptions = () => {
    const hasAnyOptionsEmpty = Object.values(optionsSelect).some(
      (item) => item
    );

    if (!hasAnyOptionsEmpty) {
      return null;
    }

    handleUpdateAllItems(optionsSelect);
    setOptionsSelect(DEFAULT_OPTIONS);
  };

  const hasSomeAgreement = (inptValue: string | DateType) => {
    if (selectedAll) {
      const hasAgreement = allItems.reduce(
        (acc, item) => {
          const agreementData = item.price.salesAgreementData;
          const extensionAttributes = item?.extension_attributes;

          const itemAgreement = checkAgreement({
            agreementData,
            extensionAttributes
          });

          if (!itemAgreement) {
            return acc;
          }

          //Date
          const endDate = agreementData.expirationDate
            ? agreementData.expirationDate.replace('Z', '')
            : agreementData.expirationDate;

          const selectedDate = moment(inptValue);
          const hasDateAgreement =
            endDate !== '' &&
            !moment(inptValue).isSame(
              extensionAttributes.expected_date_gerdau
            ) &&
            selectedDate.isAfter(endDate);

          //Destination
          const hasDestination = agreementData.destination !== inptValue;

          //Payment
          const hasPayment = agreementData.paymentTerms !== inptValue;

          //TaxBenefits
          const hasTaxBenefits =
            extensionAttributes.tax_benefit_gerdau !== inptValue;

          return {
            date: hasDateAgreement,
            destination: hasDestination,
            payment: hasPayment,
            fiscalBenefit: hasTaxBenefits
          };
        },
        {
          date: false,
          destination: false,
          payment: false,
          fiscalBenefit: false
        }
      );

      return hasAgreement;
    } else {
      const selected =
        allItems.filter(
          (item) => item.item_id && selectedItems.includes(item.item_id)
        ) ?? [];
      const hasAgreement = selected.reduce(
        (acc, item) => {
          const agreementData = item.price.salesAgreementData;
          const extensionAttributes = item?.extension_attributes;

          const itemAgreement = checkAgreement({
            agreementData,
            extensionAttributes
          });

          if (!itemAgreement) {
            return acc;
          }

          //Date
          const endDate = agreementData.expirationDate
            ? agreementData.expirationDate.replace('Z', '')
            : agreementData.expirationDate;

          const selectedDate = moment(inptValue);
          const hasDateAgreement =
            endDate !== '' &&
            !moment(inptValue).isSame(
              extensionAttributes.expected_date_gerdau
            ) &&
            selectedDate.isAfter(endDate);

          //Destination
          const hasDestination = agreementData.destination !== inptValue;

          //Payment
          const hasPayment = agreementData.paymentTerms !== inptValue;

          //TaxBenefits
          const hasTaxBenefits =
            extensionAttributes.tax_benefit_gerdau !== inptValue;

          return {
            date: hasDateAgreement,
            destination: hasDestination,
            payment: hasPayment,
            fiscalBenefit: hasTaxBenefits
          };
        },
        {
          date: false,
          destination: false,
          payment: false,
          fiscalBenefit: false
        }
      );
      return hasAgreement;
    }
  };

  const handleMiddleware = ({
    value,
    type
  }: {
    value: string | DateType;
    type: 'date' | 'destination' | 'payment' | 'fiscalBenefit';
  }) => {
    const hasAgreementItems = hasSomeAgreement(value);
    const handleTaggingModalOutAgreedPrice = (labelButton: string) => {
      dispatch(
        sendTagging({
          event: 'fora_do_preco_acordado',
          tipo_compra: tipo_compra,
          mensagem: validationDescription[type],
          botao_navegacao: labelButton
        })
      );
    };
    if (type === 'date') {
      if (value && isDateBeyondMaxDelivery(value as Date)) {
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message:
              'A data desejada excede o prazo permitido. Escolha uma data dentro dos próximos 2 anos.',
            durationInMS: 5000
          })
        );
        setOptionsSelect((prev) => ({ ...prev, date: null }));
        return;
      }
      if (hasAgreementItems.date) {
        setValidationModalInformation((prev) => ({
          ...prev,
          title: validationTitle[type],
          description: validationDescription[type],
          show: true,
          handleNextBtn: () => {
            setOptionsSelect((prev) => ({ ...prev, date: value as DateType }));
            handleTaggingModalOutAgreedPrice('Alterar');
            return setValidationModalInformation((prev) => ({
              ...prev,
              show: false
            }));
          },
          handleCloseValidationModal: () => {
            setOptionsSelect((prev) => ({ ...prev, date: undefined }));
            handleTaggingModalOutAgreedPrice('Cancelar');
            return setValidationModalInformation((prev) => ({
              ...prev,
              show: false
            }));
          }
        }));
      }

      return setOptionsSelect((prev) => ({ ...prev, date: value as DateType }));
    }

    if (type === 'destination') {
      if (hasAgreementItems.destination) {
        setValidationModalInformation((prev) => ({
          ...prev,
          title: validationTitle[type],
          description: validationDescription[type],
          show: true,
          handleNextBtn: () => {
            setOptionsSelect((prev) => ({
              ...prev,
              destination: value as string
            }));
            handleTaggingModalOutAgreedPrice('Alterar');
            return setValidationModalInformation((prev) => ({
              ...prev,
              show: false
            }));
          },
          handleCloseValidationModal: () => {
            setOptionsSelect((prev) => ({ ...prev, destination: '' }));
            handleTaggingModalOutAgreedPrice('Alterar');
            return setValidationModalInformation((prev) => ({
              ...prev,
              show: false
            }));
          }
        }));
      }

      return setOptionsSelect((prev) => ({
        ...prev,
        destination: value as string
      }));
    }

    if (type === 'payment') {
      if (hasAgreementItems.payment) {
        setValidationModalInformation((prev) => ({
          ...prev,
          title: validationTitle[type],
          description: validationDescription[type],
          show: true,
          handleNextBtn: () => {
            setOptionsSelect((prev) => ({
              ...prev,
              payment: value as string
            }));
            handleTaggingModalOutAgreedPrice('Alterar');
            return setValidationModalInformation((prev) => ({
              ...prev,
              show: false
            }));
          },
          handleCloseValidationModal: () => {
            setOptionsSelect((prev) => ({ ...prev, payment: '' }));
            handleTaggingModalOutAgreedPrice('Alterar');
            return setValidationModalInformation((prev) => ({
              ...prev,
              show: false
            }));
          }
        }));
      }

      return setOptionsSelect((prev) => ({
        ...prev,
        payment: value as string
      }));
    }

    if (type === 'fiscalBenefit') {
      if (hasAgreementItems.fiscalBenefit) {
        setValidationModalInformation((prev) => ({
          ...prev,
          title: validationTitle[type],
          description: validationDescription[type],
          show: true,
          handleNextBtn: () => {
            setOptionsSelect((prev) => ({
              ...prev,
              fiscalBenefits: value as string
            }));
            handleTaggingModalOutAgreedPrice('Alterar');
            return setValidationModalInformation((prev) => ({
              ...prev,
              show: false
            }));
          },
          handleCloseValidationModal: () => {
            setOptionsSelect((prev) => ({ ...prev, fiscalBenefits: '' }));
            handleTaggingModalOutAgreedPrice('Alterar');
            return setValidationModalInformation((prev) => ({
              ...prev,
              show: false
            }));
          }
        }));
      }

      return setOptionsSelect((prev) => ({
        ...prev,
        fiscalBenefits: value as string
      }));
    }
  };
  return (
    <ProductTableContainer ref={shoppingCartRef}>
      <ValidationModal {...validationModalInformation} />
      <Container bgWhite>
        <RowActionContainer withoutPadding actionHeader>
          <BoxActionContainer {...(selectedAll && { actionHeader: true })}>
            <Checkbox
              checked={selectedAll}
              onChange={handleSelectAll}
              name="cartItemselector"
              label="Selecionar todos"
              ariaLabel="Selecionar todos"
              disabled={loading || itemsTable.items.length === 0}
            />
            <ChipSelectedItems
              allItems={allItems.length}
              selectedItems={selectedItems.length}
              selectedAll={selectedAll}
            />
          </BoxActionContainer>
          <BoxActionContainer>
            <DateField
              helperTextTooltipPlacement={'bottomLeft'}
              helperText="Escolha um dia útil dentro dos próximos 2 anos."
              onChange={(e: DateType) =>
                handleMiddleware({ value: e, type: 'date' })
              }
              selected={optionsSelect.date}
              label="Data desejada"
              name="inputField"
              disabled={!selectedSome || loading}
              disableWeekend
              minDate={minDate}
            />
            <Select
              onSelect={(e: string | string[]) => {
                if (Array.isArray(e)) {
                  return handleMiddleware({ value: e[0], type: 'destination' });
                }

                handleMiddleware({ value: e, type: 'destination' });
              }}
              options={destinationsOpt}
              label="Destinação"
              name="destination"
              value={optionsSelect.destination}
              disabled={!selectedSome || loading}
            />
            {!newCartEndpoint && (
              <Select
                options={paymentsTermOpt}
                onSelect={(e: string | string[]) => {
                  if (Array.isArray(e)) {
                    return handleMiddleware({ value: e[0], type: 'payment' });
                  }

                  handleMiddleware({ value: e, type: 'payment' });
                }}
                label="Pagamento"
                name="payment"
                value={optionsSelect.payment}
                disabled={!selectedSome || loading}
              />
            )}

            {fiscalBenefits.length > 0 && (
              <Select
                options={fiscalBenefitsOpt}
                onSelect={(e: string | string[]) => {
                  if (Array.isArray(e)) {
                    return handleMiddleware({
                      value: e[0],
                      type: 'fiscalBenefit'
                    });
                  }

                  handleMiddleware({ value: e, type: 'fiscalBenefit' });
                }}
                label="Benefício fiscal"
                name="taxBenefit"
                value={
                  fiscalBenefits.length === 0
                    ? 'Indisponível'
                    : optionsSelect.fiscalBenefits
                }
                disabled={
                  !selectedSome || loading || fiscalBenefits.length !== 1
                }
              />
            )}
          </BoxActionContainer>
        </RowActionContainer>
        <RowActionContainer>
          <BoxActionContainer>
            <Text>
              Selecione um ou mais produtos do seu carrinho para alterá-los em
              lote.
            </Text>
          </BoxActionContainer>
          <BoxActionContainer>
            <Button
              name="Aplicar"
              style={{ width: '172px' }}
              fullWidth
              variant="secondary"
              onClick={handleUpdateWithAllOptions}
              disabled={!selectedSome || loading}
            >
              Aplicar alterações
            </Button>
            <Button
              name="Deletar"
              variant="danger"
              icon={<Icon option="Trash" />}
              onClick={handleOpenModal}
              disabled={!selectedSome || loading}
            />
          </BoxActionContainer>
        </RowActionContainer>
      </Container>

      {itemsTable.items.map((product) => (
        <Product
          key={product.sku}
          handleUpdateItem={handleUpdateItem}
          handleDuplicateItem={handleDuplicateItem}
          handleSelectProduct={handleSelectProduct}
          handleDeleteItem={handleDeleteItem}
          handleUpdateAllItems={handleUpdateAllItems}
          handleUpdateSingleItem={handleUpdateSingleItem}
          product={product}
          dirtyProduct={dirtyCartItems?.find(
            (dirty) => dirty.item_id === product.item_id
          )}
          selected={selectedItems.some((item) => item === product.item_id)}
          isBlockedCustomer={isBlockedCustomer}
          loading={loading}
          syncingFields={syncingFields}
          minDate={minDate}
          hasSomeProductsWithBenefit={fiscalBenefits.length > 0}
          allItemsFailed={allItemsFailed}
          isFavorited={isFavoriteEnabled && favoritedSkus.has(product.sku)}
          onFavoriteToggle={
            isFavoriteEnabled ? handleToggleFavorite : undefined
          }
        />
      ))}

      <ProductCartModal
        openModal={openModal}
        handleModalAction={handleModalAction}
      />
    </ProductTableContainer>
  );
};
export default CartProductTable;
